import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Wind, BookOpen, AlertTriangle, Headphones, Music, ArrowRight, Heart, Shield, Clock, ExternalLink, Phone, Mail, Send, CheckCircle, Users, Loader2, MessageCircle, Unlock } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";

const API = `/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const stagger = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.12 } }
};

const BreathingOrb = () => {
  const [phase, setPhase] = useState('inspire');
  useEffect(() => {
    const cycle = () => {
      setPhase('inspire');
      setTimeout(() => setPhase('expire'), 4000);
    };
    cycle();
    const interval = setInterval(cycle, 12000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex items-center justify-center h-40">
      <motion.div
        animate={{
          scale: phase === 'inspire' ? 1.5 : 1,
          backgroundColor: phase === 'inspire' ? '#6B8576' : '#D9A892',
        }}
        transition={{ duration: phase === 'inspire' ? 4 : 8, ease: "easeInOut" }}
        className="w-16 h-16 rounded-full opacity-80"
        style={{ boxShadow: `0 0 40px ${phase === 'inspire' ? 'rgba(107,133,118,0.3)' : 'rgba(217,168,146,0.3)'}` }}
      />
    </div>
  );
};

const ContactForm = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', message: '' });
    } catch { setStatus('error'); }
    finally { setSending(false); }
  };

  const inputCls = "w-full px-4 py-3.5 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 text-sm";

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
      <input type="text" placeholder="Votre nom" className={inputCls} value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="contact-name" />
      <input type="email" placeholder="Votre email" className={inputCls} value={form.email} onChange={e => setForm({...form, email: e.target.value})} required data-testid="contact-email" />
      <textarea placeholder="Votre message..." className={`${inputCls} min-h-[120px] resize-y`} value={form.message} onChange={e => setForm({...form, message: e.target.value})} required data-testid="contact-message" />
      {status === 'success' && <div className="flex items-center gap-2 text-[#6B8576] text-sm"><CheckCircle className="w-4 h-4" strokeWidth={1.5} /> Message envoyé ! Je vous répondrai rapidement.</div>}
      {status === 'error' && <div className="text-[#B86666] text-sm">Erreur lors de l'envoi. Réessayez ou contactez-moi directement.</div>}
      <button type="submit" disabled={sending} className="w-full py-3.5 bg-[#6B8576] text-white rounded-full font-medium hover:bg-[#546A5E] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2 text-sm" data-testid="contact-submit">
        {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" strokeWidth={1.5} /> Envoyer</>}
      </button>
    </form>
  );
};

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Header */}
      <header className="bg-[#FAF9F6]/90 backdrop-blur-md border-b border-[#6B8576]/10 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-[#6B8576] rounded-2xl flex items-center justify-center">
              <Music className="w-4 h-4 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-semibold text-[#2A3A31]" style={{ fontFamily: 'Outfit' }}>Audio Sur Mesure</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="#contact" className="hidden sm:inline-flex px-4 py-2 text-[#6A7771] hover:text-[#2A3A31] text-sm font-medium transition-colors duration-200">Contact</a>
            <Link to="/login" className="px-5 py-2 border border-[#6B8576] text-[#2A3A31] hover:bg-[#6B8576]/5 rounded-full text-sm font-medium transition-all duration-300" data-testid="nav-login-button">Se connecter</Link>
            <Link to="/register" className="px-5 py-2 bg-[#6B8576] hover:bg-[#5A7365] text-white rounded-full text-sm font-medium transition-all duration-300" data-testid="nav-signup-button">S'inscrire</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="lg:col-span-7"
            >
              <p className="text-sm font-medium tracking-wide uppercase text-[#6B8576] mb-4" style={{ fontFamily: 'Manrope' }}>Votre espace de mieux-être</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-[#2A3A31] leading-[1.1] mb-6" style={{ fontFamily: 'Outfit' }}>
                Un espace pour vous,<br />
                <span className="font-normal">à votre rythme</span>
              </h1>
              <p className="text-lg text-[#5C6A61] leading-relaxed max-w-xl mb-10" style={{ fontFamily: 'Manrope' }}>
                Des outils concrets pour traverser les moments difficiles, reprendre du pouvoir sur vos pensées et avancer pas à pas. Accessible quand vous en avez besoin, où que vous soyez.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register" className="px-8 py-4 bg-[#6B8576] hover:bg-[#5A7365] text-white rounded-full font-medium transition-all duration-300 flex items-center gap-2 group" data-testid="hero-signup-button">
                  Commencer <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" strokeWidth={1.5} />
                </Link>
                <Link to="/login" className="px-8 py-4 border border-[#6B8576] text-[#2A3A31] hover:bg-[#6B8576]/5 rounded-full font-medium transition-all duration-300">
                  Se connecter
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              className="lg:col-span-5 hidden lg:block"
            >
              <div className="rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(42,58,49,0.1)]">
                <img src="https://images.unsplash.com/photo-1762539683985-de22c1e2358f?w=600&q=80" alt="Chemin en forêt, lumière apaisante" className="w-full h-[400px] object-cover" loading="lazy" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features - Bento Grid */}
      <section className="py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-14">
            <p className="text-sm font-medium tracking-wide uppercase text-[#6B8576] mb-3" style={{ fontFamily: 'Manrope' }}>Vos outils gratuits</p>
            <h2 className="text-3xl sm:text-4xl font-normal tracking-tight text-[#2A3A31]" style={{ fontFamily: 'Outfit' }}>Tout ce dont vous avez besoin,<br />au même endroit</h2>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid grid-cols-1 md:grid-cols-12 gap-5">
            <motion.div variants={fadeUp} className="md:col-span-8" data-testid="feature-card-exercices">
              <div className="bg-white border border-[#6B8576]/10 rounded-3xl p-8 sm:p-12 h-full shadow-[0_8px_30px_rgba(42,58,49,0.04)] hover:-translate-y-1 transition-all duration-300">
                <BookOpen className="w-10 h-10 text-[#6B8576] mb-5" strokeWidth={1.5} />
                <h3 className="text-2xl font-medium tracking-tight text-[#2A3A31] mb-2" style={{ fontFamily: 'Outfit' }}>14 exercices pratiques</h3>
                <p className="text-[#5C6A61] leading-relaxed max-w-lg" style={{ fontFamily: 'Manrope' }}>
                  Mission sabotage, fiche Sedona, jeu des paradoxes... Des exercices concrets inspirés de la thérapie stratégique et de la psychologie sociale pour mieux comprendre vos émotions et dépasser vos blocages.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="md:col-span-4" data-testid="feature-card-coherence">
              <div className="bg-gradient-to-br from-[#6B8576] to-[#5A7365] rounded-3xl p-8 h-full text-white shadow-[0_8px_30px_rgba(42,58,49,0.1)] hover:-translate-y-1 transition-all duration-300">
                <Wind className="w-10 h-10 mb-4 opacity-80" strokeWidth={1.5} />
                <h3 className="text-xl font-medium tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>Cohérence cardiaque</h3>
                <p className="text-white/70 text-sm leading-relaxed mb-4" style={{ fontFamily: 'Manrope' }}>Respiration guidée 4s / 8s pour calmer votre système nerveux.</p>
                <BreathingOrb />
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="md:col-span-5" data-testid="feature-card-crise">
              <div className="bg-gradient-to-br from-[#D9A892] to-[#C89680] rounded-3xl p-8 sm:p-10 h-full text-white shadow-[0_8px_30px_rgba(217,168,146,0.15)] hover:-translate-y-1 transition-all duration-300">
                <AlertTriangle className="w-10 h-10 mb-5 opacity-80" strokeWidth={1.5} />
                <h3 className="text-2xl font-medium tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>Carnet de crise</h3>
                <p className="text-white/80 leading-relaxed" style={{ fontFamily: 'Manrope' }}>
                  Notez vos crises d'angoisse : déclencheur, symptômes, intensité. Comprendre ses crises, c'est commencer à les apprivoiser.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="md:col-span-7" data-testid="feature-card-audios">
              <div className="bg-[#F3F0EA] border border-[#6B8576]/10 rounded-3xl p-8 sm:p-12 h-full shadow-[0_8px_30px_rgba(42,58,49,0.04)] hover:-translate-y-1 transition-all duration-300">
                <Headphones className="w-10 h-10 text-[#D9A892] mb-5" strokeWidth={1.5} />
                <h3 className="text-2xl font-medium tracking-tight text-[#2A3A31] mb-2" style={{ fontFamily: 'Outfit' }}>Séances audio personnalisées</h3>
                <p className="text-[#5C6A61] leading-relaxed max-w-lg" style={{ fontFamily: 'Manrope' }}>
                  Écoutez des séances d'hypnose et de relaxation pré-enregistrées, débloquées progressivement par votre therapeute selon votre avancement.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Interactive Tools Section */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-14">
            <p className="text-sm font-medium tracking-wide uppercase text-[#D9A892] mb-3" style={{ fontFamily: 'Manrope' }}>Nouveautés</p>
            <h2 className="text-3xl sm:text-4xl font-normal tracking-tight text-[#2A3A31] mb-3" style={{ fontFamily: 'Outfit' }}>Des outils qui vous accompagnent<br />dans les moments clés</h2>
            <p className="text-[#5C6A61] max-w-2xl" style={{ fontFamily: 'Manrope' }}>Chaque outil a été conçu pour un besoin précis. Pas de diagnostic, pas de score. Juste un appui concret, quand vous en avez besoin.</p>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "SOS parent", desc: "Reprendre le contrôle quand la tension monte avec un enfant.", color: "bg-[#B86666]/8", iconColor: "text-[#B86666]", Icon: Heart },
              { title: "Boucliers Paradoxaux", desc: "Transformer une attaque en occasion de se protéger autrement.", color: "bg-[#6B8576]/8", iconColor: "text-[#6B8576]", Icon: Shield },
              { title: "Accusation retournée", desc: "Sortir du reproche pour reprendre sa responsabilité.", color: "bg-[#D9A892]/10", iconColor: "text-[#D9A892]", Icon: MessageCircle },
              { title: "Pour le moment", desc: "Défiger une pensée qui commence à ressembler à une identité.", color: "bg-[#6B8576]/8", iconColor: "text-[#6B8576]", Icon: Unlock },
            ].map((tool, i) => (
              <motion.div key={i} variants={fadeUp} className="bg-[#FAF9F6] border border-[#EBE7E0] rounded-3xl p-6 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(42,58,49,0.06)] transition-all duration-300">
                <div className={`w-10 h-10 ${tool.color} rounded-2xl flex items-center justify-center mb-4`}>
                  <tool.Icon className={`w-5 h-5 ${tool.iconColor}`} strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-semibold text-[#2A3A31] mb-2" style={{ fontFamily: 'Outfit' }}>{tool.title}</h3>
                <p className="text-sm text-[#5C6A61] leading-relaxed" style={{ fontFamily: 'Manrope' }}>{tool.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-10 text-center">
            <Link to="/register" className="inline-flex items-center gap-2 px-8 py-4 bg-[#6B8576] hover:bg-[#5A7365] text-white rounded-full font-medium transition-all duration-300 group text-sm" data-testid="tools-cta">
              Accéder à tous les outils <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* About Lynda */}
      <section className="py-24 sm:py-32 bg-white" data-testid="about-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <p className="text-sm font-medium tracking-wide uppercase text-[#6B8576] mb-3" style={{ fontFamily: 'Manrope' }}>Votre thérapeute</p>
              <h2 className="text-3xl sm:text-4xl font-normal tracking-tight text-[#2A3A31] mb-6" style={{ fontFamily: 'Outfit' }}>Lynda Bry-Bonnard</h2>
              <p className="text-lg text-[#D9A892] font-medium italic mb-6" style={{ fontFamily: 'Outfit' }}>
                "Moins je vous vois, mieux je me porte" : la phrase que mes clients entendent le plus souvent.
              </p>
              <div className="space-y-4 text-[#5C6A61] leading-relaxed" style={{ fontFamily: 'Manrope' }}>
                <p>
                  Ici, pas de bla-bla inutile ni de thérapie interminable. Mon objectif : vous aider à avancer rapidement et efficacement.
                </p>
                <p>
                  Attendez-vous à être surpris, parfois bousculé, mais toujours écouté. On rit, on pleure, on réfléchit et surtout, on agit. Pas de mysticisme ni de boules de cristal, mais des outils solides : hypnose, EMDR et exercices pratiques concrets.
                </p>
                <p>
                  Spécialiste EMDR, adhérente au SDMH (Syndicat des Métiers de l'Hypnose). Basée à Cognac, je reçois aussi en téléconsultation.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="https://lyndabrybonnard.fr/" target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-[#6B8576] text-white rounded-full font-medium text-sm hover:bg-[#546A5E] transition-all duration-300 flex items-center gap-2" data-testid="link-website">
                  Mon site <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                </a>
                <a href="https://lyndabrybonnard.fr/rendez-vous/" target="_blank" rel="noopener noreferrer" className="px-6 py-3 border border-[#6B8576] text-[#2A3A31] rounded-full font-medium text-sm hover:bg-[#6B8576]/5 transition-all duration-300" data-testid="link-rdv">
                  Prendre rendez-vous
                </a>
              </div>
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <div className="space-y-5">
                {[
                  { icon: Shield, text: "Espace 100% privé et sécurisé" },
                  { icon: Heart, text: "Exercices validés par une professionnelle" },
                  { icon: Clock, text: "Accessible 24h/24, à votre rythme" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 bg-[#FAF9F6] rounded-2xl p-4">
                    <div className="w-11 h-11 bg-[#6B8576]/8 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#6B8576]" strokeWidth={1.5} />
                    </div>
                    <p className="text-[#2A3A31] font-medium" style={{ fontFamily: 'Manrope' }}>{item.text}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Aller plus loin */}
      <section className="py-24 sm:py-32" data-testid="go-further-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-14">
            <p className="text-sm font-medium tracking-wide uppercase text-[#6B8576] mb-3" style={{ fontFamily: 'Manrope' }}>Aller plus loin</p>
            <h2 className="text-3xl sm:text-4xl font-normal tracking-tight text-[#2A3A31]" style={{ fontFamily: 'Outfit' }}>Ces outils vous aident,<br />un professionnel peut faire la différence</h2>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div variants={fadeUp} className="bg-white border border-[#6B8576]/10 rounded-3xl p-8 sm:p-10 shadow-[0_8px_30px_rgba(42,58,49,0.04)]">
              <div className="w-12 h-12 bg-[#D9A892]/15 rounded-2xl flex items-center justify-center mb-5">
                <Phone className="w-6 h-6 text-[#D9A892]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-medium text-[#2A3A31] mb-3" style={{ fontFamily: 'Outfit' }}>Séance en visio avec moi</h3>
              <p className="text-[#5C6A61] leading-relaxed mb-6" style={{ fontFamily: 'Manrope' }}>
                Vous souhaitez aller plus loin ? Je vous accompagne en téléconsultation. Hypnose, EMDR, thérapie brève : un accompagnement direct et efficace, où que vous soyez.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="tel:+33664260050" className="px-5 py-2.5 bg-[#6B8576] text-white rounded-full font-medium text-sm hover:bg-[#546A5E] transition-all duration-300 flex items-center gap-2">
                  <Phone className="w-4 h-4" strokeWidth={1.5} /> 06 64 26 00 50
                </a>
                <a href="https://wa.me/33664260050" target="_blank" rel="noopener noreferrer" className="px-5 py-2.5 border border-[#6B8576] text-[#2A3A31] rounded-full font-medium text-sm hover:bg-[#6B8576]/5 transition-all duration-300">
                  WhatsApp
                </a>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="bg-white border border-[#6B8576]/10 rounded-3xl p-8 sm:p-10 shadow-[0_8px_30px_rgba(42,58,49,0.04)]">
              <div className="w-12 h-12 bg-[#6B8576]/10 rounded-2xl flex items-center justify-center mb-5">
                <Users className="w-6 h-6 text-[#6B8576]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-medium text-[#2A3A31] mb-3" style={{ fontFamily: 'Outfit' }}>Trouvez un praticien près de chez vous</h3>
              <p className="text-[#5C6A61] leading-relaxed mb-6" style={{ fontFamily: 'Manrope' }}>
                Vous n'êtes pas en région de Cognac ? Ces exercices sont un complément précieux, mais rien ne remplace un accompagnement en personne. Trouvez un hypnothérapeute ou un praticien EMDR près de chez vous.
              </p>
              <a href="https://searchypnose.fr/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-[#6B8576] text-white rounded-full font-medium text-sm hover:bg-[#546A5E] transition-all duration-300 mb-4" data-testid="link-searchypnose">
                Annuaire SearchHypnose <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <p className="text-sm text-[#6A7771] italic" style={{ fontFamily: 'Manrope' }}>
                Praticiens : cette application est gratuite pour vos clients. Contactez-moi pour en savoir plus.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Pour les professionnels */}
      <section className="py-16 bg-[#2A3A31]" data-testid="pro-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <p className="text-[#6B8576] text-sm font-medium tracking-wide uppercase mb-3" style={{ fontFamily: 'Manrope' }}>Pour les professionnels</p>
            <h2 className="text-2xl sm:text-3xl font-normal tracking-tight text-white mb-4" style={{ fontFamily: 'Outfit' }}>Vous êtes thérapeute ou praticien ?</h2>
            <p className="text-white/60 leading-relaxed max-w-xl mx-auto mb-8" style={{ fontFamily: 'Manrope' }}>
              Audio Sur Mesure est gratuit pour vos clients. Offrez-leur un espace d'exercices de mieux-être entre vos séances. Contactez-moi pour en savoir plus.
            </p>
            <a href="#contact" className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#6B8576] hover:bg-[#5A7365] text-white rounded-full font-medium transition-all duration-300 text-sm">
              <Mail className="w-4 h-4" strokeWidth={1.5} /> Me contacter
            </a>
          </motion.div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24 sm:py-32" data-testid="contact-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <p className="text-sm font-medium tracking-wide uppercase text-[#6B8576] mb-3" style={{ fontFamily: 'Manrope' }}>Contact</p>
              <h2 className="text-3xl sm:text-4xl font-normal tracking-tight text-[#2A3A31] mb-6" style={{ fontFamily: 'Outfit' }}>Une question ? Un doute ?<br />Ne restez pas dans l'incertitude</h2>
              <p className="text-[#5C6A61] leading-relaxed mb-8" style={{ fontFamily: 'Manrope' }}>
                Je répondrai dès que possible. Pas de prise de rendez-vous par message : pour ça, rendez-vous sur mon site.
              </p>
              <div className="space-y-4">
                <a href="mailto:brylynda@gmail.com" className="flex items-center gap-3 text-[#2A3A31] hover:text-[#6B8576] transition-colors duration-200">
                  <div className="w-10 h-10 bg-[#6B8576]/8 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#6B8576]" strokeWidth={1.5} />
                  </div>
                  <span className="font-medium" style={{ fontFamily: 'Manrope' }}>brylynda@gmail.com</span>
                </a>
                <a href="tel:+33664260050" className="flex items-center gap-3 text-[#2A3A31] hover:text-[#6B8576] transition-colors duration-200">
                  <div className="w-10 h-10 bg-[#6B8576]/8 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#6B8576]" strokeWidth={1.5} />
                  </div>
                  <span className="font-medium" style={{ fontFamily: 'Manrope' }}>06 64 26 00 50</span>
                </a>
                <a href="https://lyndabrybonnard.fr/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-[#2A3A31] hover:text-[#6B8576] transition-colors duration-200">
                  <div className="w-10 h-10 bg-[#6B8576]/8 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <ExternalLink className="w-5 h-5 text-[#6B8576]" strokeWidth={1.5} />
                  </div>
                  <span className="font-medium" style={{ fontFamily: 'Manrope' }}>lyndabrybonnard.fr</span>
                </a>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <div className="bg-white rounded-3xl border border-[#EBE7E0] p-8 shadow-[0_10px_40px_rgba(42,58,49,0.06)]">
                <h3 className="font-semibold text-[#2A3A31] mb-6" style={{ fontFamily: 'Outfit' }}>Envoyez-moi un message</h3>
                <ContactForm />
                <p className="text-xs text-[#6A7771] mt-4 leading-relaxed italic">Je vous répondrai par email depuis brylynda@gmail.com — pensez à vérifier vos spams !</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl font-normal tracking-tight text-[#2A3A31] mb-5" style={{ fontFamily: 'Outfit' }}>Prêt·e à commencer ?</h2>
            <p className="text-lg text-[#5C6A61] mb-10 leading-relaxed" style={{ fontFamily: 'Manrope' }}>
              Inscrivez-vous gratuitement et commencez votre parcours de bien-être dès aujourd'hui.
            </p>
            <Link to="/register" className="inline-flex items-center gap-2 px-10 py-4 bg-[#6B8576] hover:bg-[#5A7365] text-white rounded-full font-medium transition-all duration-300 group text-lg">
              S'inscrire <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2A3A31] text-white/70 py-12" data-testid="footer-contact">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-[#6B8576] rounded-xl flex items-center justify-center">
                <Music className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
              <span className="text-white font-semibold" style={{ fontFamily: 'Outfit' }}>Audio Sur Mesure</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a href="https://lyndabrybonnard.fr/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-200">lyndabrybonnard.fr</a>
              <a href="https://www.facebook.com/brybonnardlynda" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-200">Facebook</a>
              <a href="https://www.linkedin.com/in/lynda-bry-bonnard-28ba3895/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-200">LinkedIn</a>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/40">Lynda Bry-Bonnard - Hypnose et EMDR - 1 rue des écoles, 16100 Javrezac - Adhérente SDMH</p>
            <div className="flex items-center gap-4 text-xs text-white/40">
              <Link to="/mentions-legales" className="hover:text-white/70 transition-colors duration-200">Mentions légales</Link>
              <Link to="/cgu" className="hover:text-white/70 transition-colors duration-200">CGU</Link>
              <Link to="/confidentialite" className="hover:text-white/70 transition-colors duration-200">Confidentialité</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
